// app/components/roles/RoleModal.jsx
import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';

const RoleModal = ({ isOpen, onClose, onSave, role }) => {
    const [formData, setFormData] = useState({
        role_code: '',
        role_name: '',
        description: '',
        is_active: true
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (role) {
            setFormData({
                role_code: role.role_code,
                role_name: role.role_name,
                description: role.description || '',
                is_active: role.is_active
            });
        } else {
            setFormData({
                role_code: '',
                role_name: '',
                description: '',
                is_active: true
            });
        }
        setErrors({});
    }, [role]);

    const validate = () => {
        const newErrors = {};

        if (!formData.role_code.trim()) {
            newErrors.role_code = 'Role code is required';
        } else if (!/^[A-Z_]+$/.test(formData.role_code)) {
            newErrors.role_code = 'Role code must be uppercase letters and underscores only';
        }

        if (!formData.role_name.trim()) {
            newErrors.role_name = 'Role name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        try {
            await onSave(formData);
            onClose();
        } catch (error) {
            console.error('Error saving role:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={role ? 'Edit Role' : 'Add New Role'}
            size="md"
        >
            <div className="space-y-4">
                <div className='flex gap-4 mb-7'>
                <div className='w-100'>
                    <label className="block text-sm font-normal text-black-700 mb-1">
                        Role Code
                    </label>
                    <input
                        type="text"
                        value={formData.role_code}
                        onChange={(e) => setFormData({
                            ...formData,
                            role_code: e.target.value.toUpperCase()
                        })}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.role_code ? 'border-red-500' : 'border-gray-300'
                            }`}
                        disabled={!!role}
                        placeholder="e.g., CUSTOM_ROLE"
                    />
                    {errors.role_code && (
                        <p className="mt-1 text-sm text-red-600">{errors.role_code}</p>
                    )}
                </div>

                <div className='w-100'>
                    <label className="block text-sm font-normal text-black mb-1">
                        Role Name
                    </label>
                    <input
                        type="text"
                        value={formData.role_name}
                        onChange={(e) => setFormData({ ...formData, role_name: e.target.value })}
                        className={`outline-none w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.role_name ? 'border-red-500' : 'border-gray-300'
                            }`}
                        placeholder="e.g., Custom Role"
                    />
                    {errors.role_name && (
                        <p className="mt-1 text-sm text-red-600">{errors.role_name}</p>
                    )}
                </div>
                </div>
                <div>
                    <label className="block text-sm font-normal text-black mb-1">
                        Description
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="outline-none w-full px-3 text-gray-600 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Describe the role's purpose and responsibilities"
                    />
                </div>

                <div className="flex items-center">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="mr-2 outline-none"
                        />
                        <span className="text-sm font-normal text-gray-700">Active</span>
                    </label>
                </div>

                <div className="flex justify-center gap-3 pt-1 pb-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                        {loading ? 'Saving...' : (role ? 'Update' : 'Create')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default RoleModal;